/*  @flow */
import React from 'react';
import { activities } from 'components/Activity/ActivityData';
import {
  createRequestOwnership,
  auditAsset,
  updateReassignOwnership,
  updateAllocateProject,
  updateChangeLocation,
} from 'permissions/asset';
import { allRoles } from 'permissions/general';
import ActionButton from 'components/ActionsList/components/ActionButton/ActionButton';
import { ElectroTooltip } from 'components/ExtendedUiElements/customized';

type ConditionOptions = {
  account?: boolean,
  project?: boolean,
  tooltips: {
    accountIdsDontNotMatch: string,
    hasProject: string,
  },
};

const condition = (options: ConditionOptions) => (data, action, callback, t) => {
  const uniqueAccountId = new Set(data.map(item => item.account_id));
  const accountIdsDontNotMatch = uniqueAccountId.size > 1;
  const hasProject = data.filter(item => item.project_id !== null).length > 0;
  const hasSoldAsset = data.filter(item => item.asset_status === 'Sold').length > 0;

  if (hasSoldAsset & options.project) {
    return (
      <ElectroTooltip title={t(`general:labels.tooltips.is_sold_asset`)}>
        <div>
          <ActionButton action={action} onActionDrawer={callback} disabled={hasSoldAsset} />
        </div>
      </ElectroTooltip>
    );
  }

  if (hasProject && options.project) {
    return (
      <ElectroTooltip title={t(`general:labels.tooltips.${options.tooltips.hasProject}`)}>
        <div>
          <ActionButton action={action} onActionDrawer={callback} disabled={hasProject} />
        </div>
      </ElectroTooltip>
    );
  }

  if (accountIdsDontNotMatch) {
    return (
      <ElectroTooltip
        title={t(`general:labels.tooltips.${options.tooltips.accountIdsDontNotMatch}`)}
      >
        <div>
          <ActionButton
            action={action}
            onActionDrawer={callback}
            disabled={accountIdsDontNotMatch}
          />
        </div>
      </ElectroTooltip>
    );
  }

  return <ActionButton action={action} onActionDrawer={callback} />;
};

const caasCondition = (data, action, callback, t) => {
  const uniqueAccountId = new Set(data.map(item => item.account_id));
  const accountIdsDontNotMatch = uniqueAccountId.size > 1;
  const itemsSelected = data.length;

  if (itemsSelected > 25) {
    return (
      <ElectroTooltip title={'Please select maximum 25 Assets'}>
        <div>
          <ActionButton action={action} onActionDrawer={callback} disabled={true} />
        </div>
      </ElectroTooltip>
    );
  }

  if (accountIdsDontNotMatch) {
    return (
      <ElectroTooltip title={'Please select Assets from a single Trading Account'}>
        <div>
          <ActionButton
            action={action}
            onActionDrawer={callback}
            disabled={accountIdsDontNotMatch}
          />
        </div>
      </ElectroTooltip>
    );
  }

  return <ActionButton action={action} onActionDrawer={callback} />;
};

const changeLocationCondition = (data, action, callback, t) => {
  const uniqueAccountId = new Set(data.map(item => item.account_id));
  const accountIdsDontNotMatch = uniqueAccountId.size > 1;

  const uniqueCountryNameId = new Set(data.map(item => item.country_name_id));
  const countryNameIdsDontNotMatch = uniqueCountryNameId.size > 1;

  const assetStatus = new Set(
    data.filter(item => item.asset_status != 'On Rent' && item.asset_status != 'On Lease')
  );
  const assetStatusDoesNotMatch = assetStatus.size >= 1;

  const itemsSelected = data.length;

  if (itemsSelected > 25) {
    return (
      <ElectroTooltip title={'Please select maximum 25 Assets'}>
        <div>
          <ActionButton action={action} onActionDrawer={callback} disabled={true} />
        </div>
      </ElectroTooltip>
    );
  }

  if (accountIdsDontNotMatch) {
    return (
      <ElectroTooltip title={'Please select Assets from a single Trading Account'}>
        <div>
          <ActionButton
            action={action}
            onActionDrawer={callback}
            disabled={accountIdsDontNotMatch}
          />
        </div>
      </ElectroTooltip>
    );
  }

  if (assetStatusDoesNotMatch) {
    return (
      <ElectroTooltip title={'Please select Assets on Rent or Lease'}>
        <div>
          <ActionButton
            action={action}
            onActionDrawer={callback}
            disabled={assetStatusDoesNotMatch}
          />
        </div>
      </ElectroTooltip>
    );
  }

  if (countryNameIdsDontNotMatch) {
    return (
      <ElectroTooltip title={'Please select Assets from a single Country'}>
        <div>
          <ActionButton
            action={action}
            onActionDrawer={callback}
            disabled={countryNameIdsDontNotMatch}
          />
        </div>
      </ElectroTooltip>
    );
  }

  return <ActionButton action={action} onActionDrawer={callback} />;
};

export const actionsListOwnedCaas = [
  {
    label: 'Calibration as a Service',
    icon: '#icon-caas',
    class: '',
    tag: activities.requestOwnership.tag,
    action: 'CaaSDetails',
    permissions: allRoles,
    condition: caasCondition,
  },
];

export const actionsListOwnedCaasMultiple = [
  {
    label: 'Calibration as a Service',
    icon: '#icon-caas',
    class: '',
    tag: activities.requestOwnership.tag,
    action: 'CaaSDetailsMultiple',
    permissions: allRoles,
    condition: caasCondition,
  },
];

export const actionsListOwnedSingle = [
  {
    label: 'general:actions.request_ownership',
    icon: activities.requestOwnership.icon,
    class: '',
    tag: activities.requestOwnership.tag,
    action: activities.requestOwnership.action,
    permissions: createRequestOwnership,
  },
  {
    label: 'asset:Submit Audit',
    icon: activities.assetAudit.icon,
    class: '',
    tag: activities.assetAudit.tag,
    action: activities.assetAudit.action,
    permissions: [...auditAsset],
  },
  {
    label: 'general:actions.reassign_asset',
    icon: activities.assignedUser.icon,
    class: '',
    tag: activities.assignedUser.tag,
    action: activities.assignedUser.action,
    permissions: [...updateReassignOwnership],
    condition: condition({
      account: true,
      tooltips: { accountIdsDontNotMatch: 'reassign_assets_account' },
    }),
  },
  {
    label: 'general:actions.assign_project',
    icon: activities.assignAssetToProject.icon,
    class: '',
    tag: activities.assignAssetToProject.tag,
    action: activities.assignAssetToProject.action,
    permissions: [...updateAllocateProject],
    condition: condition({
      project: true,
      tooltips: { accountIdsDontNotMatch: 'assign_project_account', hasProject: 'has_project' },
    }),
  },
];

export const actionsListOwnedMultiple = [
  {
    label: 'general:actions.request_ownership',
    icon: activities.requestOwnership.icon,
    class: '',
    tag: activities.requestOwnership.tag,
    action: 'requestOwnershipMulti',
    permissions: createRequestOwnership,
  },
  {
    label: 'asset:Submit Audit',
    icon: activities.assetAudit.icon,
    class: '',
    tag: activities.assetAudit.tag,
    action: activities.assetAudit.action,
    permissions: [...auditAsset],
  },
  {
    label: 'general:actions.reassign_asset',
    icon: activities.assignedUser.icon,
    class: '',
    tag: activities.assignedUser.tag,
    action: activities.assignedUser.action,
    permissions: [...updateReassignOwnership],
    condition: condition({
      account: true,
      tooltips: { accountIdsDontNotMatch: 'reassign_assets_account' },
    }),
  },
  {
    label: 'general:actions.assign_project',
    icon: activities.assignAssetToProject.icon,
    class: '',
    tag: activities.assignAssetToProject.tag,
    action: activities.assignAssetToProject.action,
    permissions: [...updateAllocateProject],
    condition: condition({
      project: true,
      tooltips: { accountIdsDontNotMatch: 'assign_project_account', hasProject: 'has_project' },
    }),
  },
];

export const actionsListRentedSingle = [
  {
    label: 'general:actions.request_ownership',
    icon: activities.requestOwnership.icon,
    class: '',
    tag: activities.requestOwnership.tag,
    action: activities.requestOwnership.action,
    permissions: createRequestOwnership,
  },
  {
    label: 'asset:Submit Audit',
    icon: activities.assetAudit.icon,
    class: '',
    tag: activities.assetAudit.tag,
    action: activities.assetAudit.action,
    permissions: [...auditAsset],
  },
  {
    label: 'general:actions.reassign_asset',
    icon: activities.assignedAsset.icon,
    class: '',
    tag: activities.assignedAsset.tag,
    action: activities.assignedAsset.action,
    permissions: [...updateReassignOwnership],
    condition: condition({
      account: true,
      tooltips: { accountIdsDontNotMatch: 'reassign_assets_account' },
    }),
  },
  {
    label: 'general:actions.assign_project',
    icon: activities.assignAssetToProject.icon,
    class: '',
    tag: activities.assignAssetToProject.tag,
    action: activities.assignAssetToProject.action,
    permissions: [...updateAllocateProject],
    condition: condition({
      project: true,
      tooltips: { accountIdsDontNotMatch: 'assign_project_account', hasProject: 'has_project' },
    }),
  },
  {
    label: 'general:actions.change_location',
    icon: activities.changeMultipleLocations.icon,
    class: '',
    tag: activities.changeMultipleLocations.tag,
    action: activities.changeMultipleLocations.action,
    permissions: [...updateChangeLocation],
    condition: changeLocationCondition,
  },
];

export const actionsListRentedMultiple = [
  {
    label: 'general:actions.request_ownership',
    icon: activities.requestOwnership.icon,
    class: '',
    tag: activities.requestOwnership.tag,
    action: 'requestOwnershipMulti',
    permissions: createRequestOwnership,
  },
  {
    label: 'asset:Submit Audit',
    icon: activities.assetAudit.icon,
    class: '',
    tag: activities.assetAudit.tag,
    action: activities.assetAudit.action,
    permissions: [...auditAsset],
  },
  {
    label: 'general:actions.reassign_asset',
    icon: activities.assignedAsset.icon,
    class: '',
    tag: activities.assignedAsset.tag,
    action: activities.assignedAsset.action,
    permissions: [...updateReassignOwnership],
    condition: condition({
      account: true,
      tooltips: { accountIdsDontNotMatch: 'reassign_assets_account' },
    }),
  },

  {
    label: 'general:actions.assign_project',
    icon: activities.assignAssetToProject.icon,
    class: '',
    tag: activities.assignAssetToProject.tag,
    action: activities.assignAssetToProject.action,
    permissions: [...updateAllocateProject],
    condition: condition({
      project: true,
      tooltips: { accountIdsDontNotMatch: 'assign_project_account', hasProject: 'has_project' },
    }),
  },
  {
    label: 'general:actions.change_location',
    icon: activities.changeMultipleLocations.icon,
    class: '',
    tag: activities.changeMultipleLocations.tag,
    action: activities.changeMultipleLocations.action,
    permissions: [...updateChangeLocation],
    condition: changeLocationCondition,
  },
];

export const actionsListOwnedRentedMultiple = [
  {
    label: 'general:actions.request_ownership',
    icon: activities.requestOwnership.icon,
    class: '',
    tag: activities.requestOwnership.tag,
    action: 'requestOwnershipMulti',
    permissions: createRequestOwnership,
  },
  {
    label: 'asset:Submit Audit',
    icon: activities.assetAudit.icon,
    class: '',
    tag: activities.assetAudit.tag,
    action: activities.assetAudit.action,
    permissions: [...auditAsset],
  },
  {
    label: 'general:actions.reassign_asset',
    icon: activities.assignedAsset.icon,
    class: '',
    tag: activities.assignedAsset.tag,
    action: activities.assignedAsset.action,
    permissions: [...updateReassignOwnership],
    condition: condition({
      account: true,
      tooltips: { accountIdsDontNotMatch: 'reassign_assets_account' },
    }),
  },
  {
    label: 'general:actions.assign_project',
    icon: activities.assignAssetToProject.icon,
    class: '',
    tag: activities.assignAssetToProject.tag,
    action: activities.assignAssetToProject.action,
    permissions: [...updateAllocateProject],
    condition: condition({
      project: true,
      tooltips: { accountIdsDontNotMatch: 'assign_project_account', hasProject: 'has_project' },
    }),
  },
];
